import React, { useRef, useEffect } from 'react';

/**
 * NeuralBrainBackground — A cursor-reactive neural network canvas.
 *
 * • Nodes drift slowly with gentle physics.
 * • Nodes within `LINK_DIST` are connected by translucent lines (forms a "brain" mesh).
 * • The cursor acts as a gravitational pull on nearby nodes (within `CURSOR_RADIUS`),
 *   pulling them slightly toward the pointer to create a brain-following effect.
 * • Nodes near the cursor pulse brighter, simulating active synapses.
 *
 * Performance: pure 2D canvas, ~80 nodes, requestAnimationFrame, automatically pauses
 * when the page is not visible.
 */
const NeuralBrainBackground = ({
  density = 0.00012,      // nodes per pixel² → scales with screen size
  linkDist = 140,         // px — max distance for two nodes to link
  cursorRadius = 220,     // px — cursor influence radius
  color = '0, 212, 255',  // brand-primary cyan as "r,g,b"
  goldColor = '212, 175, 55', // brand-gold for cursor-active nodes
  className = '',
}) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const rafRef = useRef(0);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const rebuild = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.max(36, Math.min(110, Math.floor(width * height * density)));
      // Preserve existing nodes when resizing, just rebalance count
      const existing = nodesRef.current;
      if (existing.length === 0 || Math.abs(existing.length - target) > 8) {
        nodesRef.current = Array.from({ length: target }).map(() => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.18,
          vy: (Math.random() - 0.5) * 0.18,
          // jitter for organic pulsing
          baseR: 1.1 + Math.random() * 1.4,
          phase: Math.random() * Math.PI * 2,
        }));
      }
    };

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    };
    const onMouseLeave = () => {
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
      mouseRef.current.active = false;
    };
    const onTouchMove = (e) => {
      if (!e.touches || !e.touches[0]) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.touches[0].clientX - rect.left;
      mouseRef.current.y = e.touches[0].clientY - rect.top;
      mouseRef.current.active = true;
    };

    const tick = (t) => {
      const nodes = nodesRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mouseActive = mouseRef.current.active;

      ctx.clearRect(0, 0, width, height);

      // Step physics
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        // Cursor gravitation
        if (mouseActive) {
          const dx = mx - n.x;
          const dy = my - n.y;
          const dist = Math.hypot(dx, dy);
          if (dist < cursorRadius && dist > 0.001) {
            const pull = (1 - dist / cursorRadius) * 0.045;
            n.vx += (dx / dist) * pull;
            n.vy += (dy / dist) * pull;
          }
        }
        // gentle drift damping
        n.vx *= 0.98;
        n.vy *= 0.98;
        // velocity cap
        const v = Math.hypot(n.vx, n.vy);
        if (v > 1.2) { n.vx = (n.vx / v) * 1.2; n.vy = (n.vy / v) * 1.2; }
        // step
        n.x += n.vx;
        n.y += n.vy;
        // wrap (toroidal — keeps the mesh always full)
        if (n.x < -10) n.x = width + 10;
        if (n.x > width + 10) n.x = -10;
        if (n.y < -10) n.y = height + 10;
        if (n.y > height + 10) n.y = -10;
      }

      // Draw links
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < linkDist * linkDist) {
            const dist = Math.sqrt(dist2);
            const baseAlpha = 1 - dist / linkDist;
            // Boost alpha if either endpoint near cursor
            let alpha = baseAlpha * 0.18;
            let strokeColor = color;
            if (mouseActive) {
              const da = Math.hypot(a.x - mx, a.y - my);
              const db = Math.hypot(b.x - mx, b.y - my);
              const minD = Math.min(da, db);
              if (minD < cursorRadius) {
                const boost = 1 - minD / cursorRadius;
                alpha = baseAlpha * (0.18 + boost * 0.55);
                if (boost > 0.55) strokeColor = goldColor;
              }
            }
            ctx.strokeStyle = `rgba(${strokeColor}, ${alpha.toFixed(3)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      const time = t * 0.001;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const pulse = 0.6 + 0.4 * Math.sin(time * 1.2 + n.phase);
        let r = n.baseR * pulse;
        let nodeColor = color;
        let nodeAlpha = 0.45;
        if (mouseActive) {
          const dm = Math.hypot(n.x - mx, n.y - my);
          if (dm < cursorRadius) {
            const boost = 1 - dm / cursorRadius;
            r = n.baseR * (pulse + boost * 1.4);
            nodeAlpha = 0.45 + boost * 0.55;
            if (boost > 0.55) nodeColor = goldColor;
          }
        }
        // glow
        ctx.fillStyle = `rgba(${nodeColor}, ${(nodeAlpha * 0.25).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 2.4, 0, Math.PI * 2);
        ctx.fill();
        // core
        ctx.fillStyle = `rgba(${nodeColor}, ${nodeAlpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rebuild();
    const onResize = () => rebuild();
    window.addEventListener('resize', onResize);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onMouseLeave);

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      } else if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density, linkDist, cursorRadius, color, goldColor]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="neural-brain-canvas"
      className={`absolute inset-0 w-full h-full pointer-events-auto ${className}`}
      style={{ display: 'block' }}
    />
  );
};

export default NeuralBrainBackground;
